import {
  memo,
  useCallback,
  use,
  useEffect,
  useMemo,
  useState,
  useTransition,
  useRef,
} from "react"
import { BlendIcon } from "lucide-react"
import {
  NumberOne,
  NumberTwo,
  NumberThree,
  TrendUp,
  Percent,
  IconProps,
} from "@phosphor-icons/react"
import { PopoverClose } from "@radix-ui/react-popover"
import {
  Button,
  ButtonProps,
  Select,
  SelectTrigger,
  SelectValue,
} from "@repo/ui"
import { useTranslation } from "react-i18next"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { TableCell, TableCellContent } from "@/components/ui/table"
import { StandardInputType } from "@/types"
import {
  RowWithInputTypeContext,
  RowWithInputTypeProvider,
} from "./popover-context"
import { isNullOrUndefinedOrEmpty } from "@/lib/utils/parsers"

type PopoverTriggerProps = React.ComponentProps<typeof PopoverTrigger>

export const InputTypeProviderProvider = ({
  children,
  defaultType,
}: {
  children: React.ReactNode
  defaultType: StandardInputType
}) => {
  const [rowType, setRowType] = useState<StandardInputType>(
    defaultType ?? `absolute`
  )
  const value = useMemo(() => ({ rowType, setRowType }), [rowType, setRowType])

  useEffect(() => {
    setRowType(defaultType ?? `absolute`)
  }, [defaultType])

  return (
    <RowWithInputTypeProvider value={value}>
      {children}
    </RowWithInputTypeProvider>
  )
}

type InputTypeIconProps = IconProps & {
  type: StandardInputType
}

export const NumberIcons = (props: IconProps) => {
  const { className, ...rest } = props
  return (
    <div className="flex items-center justify-center">
      <NumberOne
        size={14}
        weight="bold"
        className={cn(`text-blue-400 -ml-1`, className)}
        {...rest}
      />
      <NumberTwo
        size={14}
        weight="bold"
        className={cn(`text-blue-400 -ml-2`, className)}
        {...rest}
      />
      <NumberThree
        size={14}
        weight="bold"
        className={cn(`text-blue-400 -ml-2`, className)}
        {...rest}
      />
    </div>
  )
}

export const InputTypeIcon = ({
  type,
  className,
  ...props
}: InputTypeIconProps) => {
  if (type === `mix`) {
    return (
      <BlendIcon
        size={16}
        className={cn(`text-blue-400 mr-2`, className)}
        {...props}
      />
    )
  }
  if (type === `growth`) {
    return (
      <TrendUp
        size={16}
        className={cn(`text-blue-400 mr-2`, className)}
        {...props}
      />
    )
  }
  if (type === `% of gross salary`) {
    return (
      <Percent
        size={16}
        className={cn(`text-blue-400 mr-2`, className)}
        {...props}
      />
    )
  }
  return (
    <NumberIcons
      size={16}
      className={cn(`text-blue-400 mr-2`, className)}
      {...props}
    />
  )
}

type InputTypePopoverProps = {
  children:
    | React.ReactNode
    | ((props: { internalType: StandardInputType }) => React.ReactNode)
  allowedTypes?: StandardInputType[]
  isRow?: boolean
  defaultType?: StandardInputType
  onTypeChange?: (type: StandardInputType) => void
} & Omit<PopoverTriggerProps, `asChild` | "children">

export const InputTypePopover = ({
  children,
  isRow,
  defaultType,
  onTypeChange,
  allowedTypes = [`absolute`],
  ...props
}: InputTypePopoverProps) => {
  const { setRowType, rowType } = use(RowWithInputTypeContext)
  const [, startTransition] = useTransition()
  const { t: commonT } = useTranslation(`common`)

  const content = useMemo(() => {
    if (typeof children === `function`) {
      return children({ internalType: rowType })
    }
    return children
  }, [children, rowType])

  const handleSetType = useCallback(
    (t: StandardInputType) => {
      startTransition(() => {
        setRowType((old: StandardInputType) => {
          if (old !== t) {
            onTypeChange?.(t)
          }
          return t
        })
      })
    },
    [setRowType, onTypeChange]
  )
  if (props.disabled) {
    return <div className="cursor-not-allowed">{content}</div>
  }
  return (
    <Popover>
      <PopoverTrigger asChild {...props}>
        {content}
      </PopoverTrigger>
      <PopoverContent className="min-w-32 w-full p-2">
        <div className="flex flex-col gap-0.5">
          <PopoverClose asChild>
            {allowedTypes.includes(`absolute`) && (
              <button
                type="button"
                data-state={rowType === `absolute` ? `selected` : `unselected`}
                onClick={() => handleSetType(`absolute`)}
                className={cn(
                  `h-9 px-4 flex items-center justify-start min-w-9 rounded-md bg-white hover:bg-blue-10 data-[state=selected]:bg-blue-50`
                )}
              >
                <NumberIcons />
                <span className="text-sm">
                  {commonT(`table.input_type_popover.value`)}
                </span>
              </button>
            )}
          </PopoverClose>
          {allowedTypes.includes(`growth`) && (
            <PopoverClose asChild>
              <button
                type="button"
                data-state={rowType === `growth` ? `selected` : `unselected`}
                onClick={() => handleSetType(`growth`)}
                className={cn(
                  `h-9 px-4 flex items-center justify-start min-w-9 rounded-md bg-white hover:bg-blue-10 data-[state=selected]:bg-blue-50 disabled:text-gray-300 disabled:hover:bg-white disabled:cursor-not-allowed`
                )}
                disabled
              >
                {/* <TrendUp size={16} className="text-blue-400 mr-2" /> */}
                <TrendUp size={16} className="text-gray-300 mr-2" />
                <span className="text-sm">
                  {commonT(`table.input_type_popover.growth`)}
                </span>
              </button>
            </PopoverClose>
          )}
          {allowedTypes.includes(`% of gross salary`) && (
            <PopoverClose asChild>
              <button
                type="button"
                data-state={
                  rowType === `% of gross salary` ? `selected` : `unselected`
                }
                onClick={() => handleSetType(`% of gross salary`)}
                className={cn(
                  `h-9 px-4 flex items-center justify-start min-w-9 rounded-md bg-white hover:bg-blue-10 data-[state=selected]:bg-blue-50`
                )}
              >
                <Percent size={16} className="text-blue-400 mr-2" />
                <span className="text-sm">
                  {commonT(`table.input_type_popover.salary`)}
                </span>
              </button>
            </PopoverClose>
          )}
          {allowedTypes.includes(`mix`) && (
            <PopoverClose asChild>
              <button
                type="button"
                data-state={rowType === `mix` ? `selected` : `unselected`}
                onClick={() => handleSetType(`mix`)}
                className={cn(
                  `h-9 px-4 flex items-center justify-start min-w-9 rounded-md bg-white hover:bg-blue-10 data-[state=selected]:bg-blue-50 disabled:text-gray-300 disabled:hover:bg-white disabled:cursor-not-allowed`
                )}
                disabled
              >
                <BlendIcon size={16} className="text-gray-300 mr-2" />
                {/* <BlendIcon size={16} className="text-blue-400 mr-2" /> */}
                <span className="text-sm">
                  {commonT(`table.input_type_popover.mix`)}
                </span>
              </button>
            </PopoverClose>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

type InputCellPopoverButtonProps<T extends string = StandardInputType> = {
  allowedTypes: T[]
  handleSetType: (t: T) => void
  type: T
} & Omit<ButtonProps, "type">

export const InputCellPopoverButton = <T extends string = StandardInputType>({
  allowedTypes,
  type,
  handleSetType,
  ...props
}: InputCellPopoverButtonProps<T>) => {
  const { t: commonT } = useTranslation(`common`)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-blue-50 mr-2"
          {...props}
        >
          <InputTypeIcon type={type as StandardInputType} className="mr-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-32 w-full p-2">
        <div className="flex flex-col gap-0.5">
          <PopoverClose asChild>
            {allowedTypes.includes(`absolute` as T) && (
              <button
                type="button"
                data-state={type === `absolute` ? `selected` : `unselected`}
                onClick={() => handleSetType(`absolute` as T)}
                className={cn(
                  `h-9 px-4 flex items-center justify-start min-w-9 rounded-md bg-white hover:bg-blue-10 data-[state=selected]:bg-blue-50`
                )}
              >
                <NumberIcons />
                <span className="text-sm">
                  {commonT(`table.input_type_popover.value`)}
                </span>
              </button>
            )}
          </PopoverClose>
          {allowedTypes.includes(`growth` as T) && (
            <PopoverClose asChild>
              <button
                type="button"
                data-state={type === `growth` ? `selected` : `unselected`}
                onClick={() => handleSetType(`growth` as T)}
                className={cn(
                  `h-9 px-4 flex items-center justify-start min-w-9 rounded-md bg-white hover:bg-blue-10 data-[state=selected]:bg-blue-50`
                )}
              >
                <TrendUp size={16} className="text-blue-400 mr-2" />
                <span className="text-sm">
                  {commonT(`table.input_type_popover.growth`)}
                </span>
              </button>
            </PopoverClose>
          )}
          {allowedTypes.includes(`% of gross salary` as T) && (
            <PopoverClose asChild>
              <button
                type="button"
                data-state={
                  type === `% of gross salary` ? `selected` : `unselected`
                }
                onClick={() => handleSetType(`% of gross salary` as T)}
                className={cn(
                  `h-9 px-4 flex items-center justify-start min-w-9 rounded-md bg-white hover:bg-blue-10 data-[state=selected]:bg-blue-50 disabled:text-gray-300 disabled:hover:bg-white disabled:cursor-not-allowed`
                )}
              >
                <Percent size={16} className={cn(`text-blue-400 mr-2`)} />
                <span className="text-sm">
                  {commonT(`table.input_type_popover.salary`)}
                </span>
              </button>
            </PopoverClose>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

type TableCellProps = React.ComponentProps<typeof TableCell>

export const InputTypeSelector = memo(
  ({
    editable,
    disabled,
    onTypeChange,
    allowedTypes,
    className,
    ...props
  }: {
    allowedTypes: StandardInputType[]
    editable?: boolean
    disabled?: boolean
    onTypeChange?: (type: StandardInputType) => void
  } & TableCellProps) => {
    const { t: commonT } = useTranslation(`common`)

    const inputTypeTranslations = useMemo(
      () => ({
        absolute: commonT(`table.input_type_popover.value`),
        growth: commonT(`table.input_type_popover.growth`),
        "% of gross salary": commonT(
          `table.input_type_popover.% of gross salary`
        ),
        mix: commonT(`table.input_type_popover.mix`),
      }),
      [commonT]
    )

    return (
      <InputTypePopover
        isRow
        disabled={disabled}
        onTypeChange={onTypeChange}
        allowedTypes={allowedTypes}
      >
        {({ internalType }) => (
          <div {...props}>
            <TableCellContent className="w-full ml-auto text-sm border-l border-l-gray-200">
              {editable ? (
                <Select
                  value={internalType}
                  defaultValue={internalType}
                  disabled={disabled}
                >
                  <SelectTrigger
                    className={cn(`border-none bg-transparent`, className)}
                  >
                    <SelectValue className="w-full">
                      <div className="flex text-sm items-center justify-start capitalize min-w-28 w-full">
                        <InputTypeIcon type={internalType} />
                        {inputTypeTranslations[internalType]}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                </Select>
              ) : (
                <div
                  className={cn(
                    `flex text-sm items-center justify-start capitalize min-w-32 w-full px-3`,
                    disabled ? `text-gray-300` : ``
                  )}
                >
                  <InputTypeIcon
                    type={internalType}
                    className={cn(disabled ? `text-blue-200` : `text-blue-400`)}
                  />
                  {inputTypeTranslations[internalType]}
                </div>
              )}
            </TableCellContent>
          </div>
        )}
      </InputTypePopover>
    )
  }
)

export const ShowInverseValue = ({
  children,
  when,
  fallback = null,
}: {
  children: React.ReactNode
  when: boolean
  fallback?: React.ReactNode
}) => {
  const { rowType } = use(RowWithInputTypeContext)
  if (rowType !== `absolute`) {
    return when ? children : fallback
  }
  return fallback
}

export const InputTypeContainer: React.FC<React.ComponentProps<"div">> = ({
  children,
  className,
  ...props
}) => (
  <div
    className={cn(
      `flex items-center w-full`,

      className
    )}
    {...props}
  >
    {children}
  </div>
)

type GenericRow = {
  config: Record<string, { type: StandardInputType }>
  inverse_values: Record<string, any>
}

type OrderTypeManagerProps<T extends string = StandardInputType> = {
  row: GenericRow
  selectedKey: string
  rowType: T
}

export const useOrderTypeManager = <T extends string = StandardInputType>({
  row,
  selectedKey,
  rowType = `absolute` as T,
}: OrderTypeManagerProps<T>) => {
  const [customType, setCustomType] = useState<T>(
    rowType === `mix` ? (row.config[selectedKey].type as T) : rowType
  )
  const [isChangingType, startTransition] = useTransition()
  const isManualChange = useRef(false)

  const handleCustomTypeChange = useCallback(
    (t: T) => {
      isManualChange.current = true
      // TODO: Remove this once we have a proper way to test the transition
      if (import.meta.env.NODE_ENV === `test`) {
        setCustomType(t)
      } else {
        startTransition(() => {
          setCustomType(t)
        })
      }
    },
    [setCustomType]
  )

  useEffect(() => {
    if (isManualChange.current) {
      isManualChange.current = false
      return
    }
    if (rowType === `mix`) {
      if (!isNullOrUndefinedOrEmpty(row.inverse_values?.[selectedKey])) {
        setCustomType(row.config[selectedKey].type as T)
      }
    } else {
      setCustomType(rowType)
    }
  }, [row, selectedKey, rowType, isManualChange])

  return [customType, handleCustomTypeChange, isChangingType] as const
}
